// Enhanced Home Section Component for Dashboard
import React from 'react';
import { motion } from 'framer-motion';

const ModernDashboardHome = ({ onNavigate }) => {
	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1
			}
		}
	};

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: {
			y: 0,
			opacity: 1,
			transition: {
				type: "spring",
				stiffness: 100
			}
		}
	};

	const features = [
		{
			icon: 'bi-chat-dots',
			title: 'Try the Playground',
			description: 'Test our AI in an interactive chat interface. Ask financial questions and see how the API responds in real-time with your data.',
			iconColor: 'text-success',
			action: () => onNavigate('playground'),
			buttonText: 'Open Playground',
			buttonClass: 'btn-success'
		},
		{
			icon: 'bi-key',
			title: 'Get Your API Keys',
			description: 'Generate and manage your API credentials. Monitor usage, set rate limits, and integrate our Banking Intelligence API with your applications.',
			iconColor: 'text-info',
			action: () => onNavigate('api-keys'),
			buttonText: 'Manage Keys',
			buttonClass: 'btn-info'
		},
		{
			icon: 'bi-book',
			title: 'Read the Docs',
			description: 'Comprehensive guides, API references, and code examples to help you seamlessly integrate our financial AI into your applications.',
			iconColor: 'text-secondary',
			action: () => onNavigate('documentation'),
			buttonText: 'View Docs',
			buttonClass: 'btn-outline-light'
		}
	];

	return (
		<div className="container-fluid py-5 px-4">
			<motion.div
				variants={containerVariants}
				initial="hidden"
				animate="visible"
			>
				{/* Hero Section */}
				<motion.div variants={itemVariants}>
					<div className="row justify-content-center text-center mb-5">
						<div className="col-lg-8">
							<motion.div
								initial={{ scale: 0.9, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								transition={{ duration: 0.5 }}
							>
								<div className="mb-4">
									<div
										className="d-inline-flex align-items-center px-3 py-2 rounded-pill mb-3"
										style={{
											background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.2), rgba(40, 167, 69, 0.05))',
											border: '1px solid rgba(40, 167, 69, 0.3)'
										}}
									>
										<i className="bi bi-stars text-success me-2"></i>
										<span className="text-white small fw-semibold">Banking Intelligence API</span>
									</div>
								</div>

								<h1 className="display-4 fw-bold mb-4" style={{
									background: 'linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%)',
									WebkitBackgroundClip: 'text',
									WebkitTextFillColor: 'transparent',
									backgroundClip: 'text'
								}}>
									Transform Banking Data into Intelligent Insights
								</h1>

								<p className="lead text-white-50 mb-4">
									Empower your applications with AI-powered financial intelligence. Analyze transactions, provide recommendations, and help users make smarter financial decisions.
								</p>
							</motion.div>
						</div>
					</div>
				</motion.div>

				{/* Getting Started with Interactive Buttons */}
				<motion.div variants={itemVariants}>
					<div className="row mb-5 justify-content-center">
						<div className="col-lg-10">
							<div
								className="card bg-dark border-0 overflow-hidden"
								style={{
									background: 'rgba(26, 26, 26, 0.95)',
									backdropFilter: 'blur(20px)',
									boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
								}}
							>
								<div className="card-body bg-dark p-4 p-md-5">
									<div className="text-center mb-4">
										<h3 className="text-white fw-bold mb-3">
											<i className="bi bi-rocket-takeoff text-success me-2"></i>
											Getting Started
										</h3>
										<p className="text-white-50 mb-0">
											The Banking Intelligence API lets you enhance your application with AI-powered financial insights.
											Analyze transactions, provide budget recommendations, and help your users make better financial decisions.
										</p>
									</div>

									<div className="row g-4 mt-2">
										{features.map((feature, index) => (
											<div key={index} className="col-md-4">
												<motion.div
													whileHover={{ y: -5 }}
													className="text-center h-100"
												>
													<div
														className="rounded-3 p-4 mx-auto mb-3 d-flex align-items-center justify-content-center"
														style={{
															width: '80px',
															height: '80px',
															background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.2), rgba(40, 167, 69, 0.05))',
															border: '1px solid rgba(40, 167, 69, 0.3)'
														}}
													>
														<i className={`bi ${feature.icon} text-success`} style={{ fontSize: '2rem' }}></i>
													</div>
													<h5 className="text-white mb-2">{feature.title}</h5>
													<p className="text-white-50 small mb-3">{feature.description}</p>

													<button
														className={`btn ${feature.buttonClass} w-100 py-2`}
														onClick={feature.action}
														style={{
															boxShadow: feature.buttonClass === 'btn-success' ? '0 4px 16px rgba(40, 167, 69, 0.3)' : 'none',
															transition: 'all 0.3s ease'
														}}
													>
														{feature.buttonText}
														<i className="bi bi-arrow-right ms-2"></i>
													</button>
												</motion.div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</motion.div>

				{/* Stats Section */}
				<motion.div variants={itemVariants}>
					<div className="row g-4">
						<div className="col-md-4">
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="text-center p-4 rounded-3"
								style={{
									background: 'rgba(26, 26, 26, 0.95)',
									backdropFilter: 'blur(10px)',
									border: '1px solid rgba(255, 255, 255, 0.1)'
								}}
							>
								<h2 className="display-4 fw-bold text-success mb-2">99.9%</h2>
								<p className="text-white-50 mb-0">API Uptime</p>
							</motion.div>
						</div>
						<div className="col-md-4">
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="text-center p-4 rounded-3"
								style={{
									background: 'rgba(26, 26, 26, 0.95)',
									backdropFilter: 'blur(10px)',
									border: '1px solid rgba(255, 255, 255, 0.1)'
								}}
							>
								<h2 className="display-4 fw-bold text-info mb-2">&lt;200ms</h2>
								<p className="text-white-50 mb-0">Average Response Time</p>
							</motion.div>
						</div>
						<div className="col-md-4">
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="text-center p-4 rounded-3"
								style={{
									background: 'rgba(26, 26, 26, 0.95)',
									backdropFilter: 'blur(10px)',
									border: '1px solid rgba(255, 255, 255, 0.1)'
								}}
							>
								<h2 className="display-4 fw-bold text-warning mb-2">24/7</h2>
								<p className="text-white-50 mb-0">Support Available</p>
							</motion.div>
						</div>
					</div>
				</motion.div>
			</motion.div>
		</div>
	);
};

export default ModernDashboardHome;